// Workbench (D378): the kind `routine.trigger`, its body from the workbench-owned ./routineTriggers.ts, marked "Workbench (D378)" below; AIOE adds the same lines.
// Workbench (D401): the kind `workflow.start`, its body from the workbench-owned ./workflows.ts, marked "Workbench (D401)" below; AIOE added them first (0.90.0) and the workbench copies the same lines.
// Workbench (D414): `accounts` on a runtime fact, marked "Workbench (D414)" below; AIOE adds the same lines.
/**
 * MIRRORED: this file is the workbench's `shared/schemas/deviceOrders.ts` (branch d257-device-orders,
 * tip c34c3939), kept exactly where it fits; the platform's additions are marked "AIOE" below
 * (`expiresAt` and `batchId` on the wire order, the stored order and its states, the issue-time
 * refusal codes, the batch answer). No version field: every field is optional, so no schema version moves.
 *
 * AIOE (Odysseus item 2, docs/plans/2026-10-odysseus-adoption.md): the kind `skill.evaluate`, its
 * body and the answer's `result`, marked "AIOE" below; the workbench mirrors them (D279). Issued only
 * by `POST /admin/v1/catalogue/:id/evaluate` and `…/submissions/:id/evaluate`, never by the generic routes.
 *
 * AIOE (specialist evaluation, docs/plans/2026-10-specialist-evaluation.md): the kind
 * `specialist.evaluate`, its body and the answer's `result`, from AIOE-first `./evaluation.ts` and
 * marked "AIOE (evaluation)" below; the workbench mirrors them (D274). Issued only by
 * `POST /admin/v1/teams/specialists/:id/evaluate`, never by the generic routes; only nodes get it.
 *
 * AIOE (D277, AIOE 0.42.0): the kind `runner.configure`, its body, the setting specs, `facts.runner`
 * and their words, taken from the workbench's `shared/schemas/runnerOrders.ts` (branch
 * d277-runner-configure, tip 67a41fa8) byte for byte and marked "AIOE (D277)" below, so the workbench
 * folds that file into this mirror. The refusal `not_orderable` is the platform's.
 *
 * Routes (platform side, AIOE 0.37.0; docs/workbench-integration.md section 34):
 *   POST   /admin/v1/fleet/devices/:id/orders             administrators: { kind, body } -> 201 DeviceOrderRecord
 *   GET    /admin/v1/fleet/devices/:id/orders?state=      the last 100 -> DeviceOrderRecord[]
 *   DELETE /admin/v1/fleet/devices/:id/orders/:orderId    cancel a pending order -> DeviceOrderRecord (state expired)
 *   POST   /projects/v1/:id/orders                        the project's owners: { kind, body } -> 201 DeviceOrderBatch
 *   GET    /projects/v1/:id/orders?batchId=               the batch's orders and its summary -> DeviceOrderBatch
 *   DELETE /projects/v1/:id/orders/:orderId               cancel one pending order of the project's nodes
 *   POST   /fleet/v1/heartbeat                            answer gains `orders`; body gains `orders` (answers) and `facts`
 *   POST   /fleet/v1/register                             body gains `facts`
 *
 * Original header follows.
 *
 * The organisation's orders to one machine (roadmap D257, docs/plans/2026-10-node-management.md):
 * download or remove a local model, install or remove an agent runtime, configure DLP, set a
 * setting. Workbench-owned: the AIOE carries the same shapes once it builds its half; until then
 * the mirrored `fleet.ts` stays byte-identical and these ride on the workbench's own copy of the
 * heartbeat (`src/platform/fleet.ts`).
 *
 * The pattern is the agent registry's (./agentRegistry.ts), widened:
 *
 *   heartbeat answer  { orders: DeviceOrder[] }          repeated until the machine answers each
 *   heartbeat body    { orders: DeviceOrderAnswer[] }    one answer per order the platform still repeats
 *   registration      { facts: DeviceFacts }             the machine's models, runtimes and DLP state
 *
 * A machine applies an order id once (a repeat is answered from what it kept, never run again),
 * keeps answering it on every heartbeat while the platform repeats it, and forgets it once the
 * platform stops. `D170`'s `update` field stays as it is.
 *
 * Words, never ids: every answer's `message` is a sentence a person reads in the console, and the
 * machine shows the person the same sentence ("Your organisation asked this machine to download
 * Qwen 2.5 Coder 14B — done"). A refusal names the policy that refused it.
 *
 * Pure: zod and string helpers only.
 */
import { z } from "zod";
import { toolReportSchema, toolServerDefinitionSchema } from "./tools.js";
import { specialistEvaluateOrderBodySchema, specialistEvaluationResultSchema } from "./evaluation.js";
import { orgTeamMemberFoundrySchema } from "./orgTeamsV2.js";
import { describeNodeOrder, NODE_ORDER_KINDS, nodeSettingBodySchema, remoteConfigureBodySchema, routineOrderBodySchema, serviceConfigureBodySchema, updateConfigureBodySchema } from "./nodeRemote.js";
/** Workbench (D378). */
import { ROUTINE_TRIGGER_ORDER_KIND, routineTriggerOrderBodySchema } from "./routineTriggers.js";
/** Workbench (D401). */
import { WORKFLOW_START_ORDER_KIND, workflowFactsSchema, workflowStartOrderBodySchema } from "./workflows.js";
export const DEVICE_ORDER_KINDS = ["model.download", "model.remove", "runtime.install", "runtime.remove", "dlp.configure", "setting.set", "skill.evaluate", "runner.configure", "specialist.evaluate", "toolserver.verify", "foundry.publish", "foundry.unpublish", "teams.refresh", ROUTINE_TRIGGER_ORDER_KIND, WORKFLOW_START_ORDER_KIND, ...NODE_ORDER_KINDS];
export const deviceOrderKindSchema = z.enum(DEVICE_ORDER_KINDS);
/** At most this many orders on one answer are read; the rest wait for the next heartbeat. */
export const MAX_DEVICE_ORDERS = 50;
const sha256 = z.string().regex(/^[0-9a-f]{64}$/i);
/**
 * A local model by what the Local models setting pins: a Hugging Face repository and a GGUF file
 * in it (a sharded model: its first part's name, or the name without the `-0000N-of-0000M` marker),
 * with the digest when the organisation's catalogue has one. `name` is what people read.
 * `catalogued` is the platform's word: the model is on the organisation's approved list.
 * A model of the workbench's own small catalogue may be named by `id` instead of repo and file.
 */
export const modelOrderBodySchema = z
    .object({
    id: z.string().min(1).max(200).optional(),
    repo: z.string().min(1).max(200).optional(),
    file: z.string().min(1).max(300).optional(),
    sha256: sha256.optional(),
    name: z.string().min(1).max(200),
    catalogued: z.boolean().optional(),
})
    .refine((b) => !!b.id || (!!b.repo && !!b.file), { message: "a model is named by its catalogue id, or by repo and file" });
/** An agent runtime by the workbench's id (`claude-code`, `codex`, `copilot`, …); `name` is optional words. */
export const runtimeOrderBodySchema = z.object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(200).optional(),
});
/**
 * Part of the policy's `dlp` block (src/control/policyFile.ts `dlpPolicySchema`), merged into the
 * `.mojoup/policy.json` of every repository on the machine. The classifier runs on a local
 * provider only. `labels` and `purview` are the organisation's own clauses and not set this way.
 */
export const dlpOrderBodySchema = z.object({
    classify: z.boolean().optional(),
    classifier: z
        .object({
        provider: z.string().min(1).max(64).optional(),
        model: z.string().max(200).optional(),
        endpoint: z.string().max(500).optional(),
    })
        .optional(),
    onSecrets: z.enum(["allow", "redact", "block", "ask"]).optional(),
    onPii: z.enum(["allow", "redact", "block", "ask"]).optional(),
    onPromptInjection: z.enum(["allow", "block", "ask"]).optional(),
    blockSensitivity: z.array(z.enum(["public", "internal", "confidential", "restricted"])).max(4).optional(),
    onUnclassified: z.enum(["allow", "ask", "block"]).optional(),
    latencyBudgetMs: z.number().int().min(50).max(5000).optional(),
});
/** One setting, without the `mjuWorkbench.` prefix; `null` removes the machine's own value. */
export const settingOrderBodySchema = z.object({
    key: z.string().min(1).max(200),
    value: z.unknown(),
});
/**
 * AIOE (Odysseus item 2): run a skill's tasks with and without it, and have a judge grade both.
 * `entryId` is the catalogue entry's id, or `submission:<id>` for a pending submission. A
 * submission's skill comes inline (`content`, `files`, the whole body at most
 * MAX_DEVICE_ORDER_BODY_BYTES); a published one is fetched from `GET /catalogue/v1` by `entryId`.
 * `contentDigest` is catalogueContentDigest (catalogue.ts) of the skill as the platform sent or
 * published it: a node that fetched different content says so in its answer.
 * `tasks` come from the requester or the submitter's `tests.md`; none means the judge writes two to
 * five from the summary. `worker` and `judge` are model ids, the node's own defaults when left out.
 * `unrelated` are tasks the skill's description must not get it loaded for. The skill's text is
 * untrusted data to the node: it never grants anything, and an unattended run refuses any approval it meets.
 */
export const skillEvaluateTaskSchema = z.object({
    id: z.string().min(1).max(40),
    prompt: z.string().min(1).max(2000),
    expect: z.string().max(500).optional(),
});
export const skillEvaluateOrderBodySchema = z.object({
    entryId: z.string().min(1).max(120),
    name: z.string().min(1).max(200),
    summary: z.string().max(2000).optional(),
    content: z.string().optional(),
    files: z.record(z.string().min(1).max(200), z.string()).optional(),
    contentDigest: sha256.optional(),
    tasks: z.array(skillEvaluateTaskSchema).max(5),
    worker: z.string().min(1).max(200).optional(),
    judge: z.string().min(1).max(200).optional(),
    unrelated: z.array(z.object({ id: z.string().min(1).max(40), prompt: z.string().min(1).max(2000) })).max(2),
});
/** One run of one task: passed by the judge, the tokens and time it took, the judge's sentence. */
const skillEvaluationRunSchema = z.object({
    passed: z.boolean(),
    tokens: z.number().nonnegative(),
    ms: z.number().nonnegative(),
    sentence: z.string().max(500),
});
/**
 * AIOE (Odysseus item 2): what a node answers a `skill.evaluate` with, beside its `message`.
 * `necessity`: without the skill every task passes: not-needed; with it more pass: needed; the same
 * pass with fewer tokens: helps. `retrieval`: the description got the skill loaded for its own tasks
 * and not the unrelated ones (right), not for its own (missed), also for unrelated ones
 * (over-eager), or nothing tested it (untested). `sentence` is the one line a person reads:
 * "Helped in 4 of 5 tasks; the model managed 3 of 5 without it; 20 per cent fewer tokens".
 */
export const skillEvaluationResultSchema = z.object({
    worker: z.string().max(200),
    judge: z.string().max(200),
    tasks: z.array(z.object({ id: z.string().min(1).max(40), withSkill: skillEvaluationRunSchema, without: skillEvaluationRunSchema, loaded: z.boolean() })).max(5),
    unrelated: z.array(z.object({ id: z.string().min(1).max(40), loaded: z.boolean() })).max(2),
    necessity: z.enum(["needed", "helps", "not-needed"]),
    retrieval: z.enum(["right", "missed", "over-eager", "untested"]),
    verdict: z.enum(["pass", "fail"]),
    sentence: z.string().max(500),
});
// ── AIOE (Hermes item 1): a tool server verified on a node ──────────────
/**
 * What the platform sends a node to check a tool server (docs/plans/2026-10-hermes-adoption.md, item 1): the
 * catalogue entry and the server's definition, environment and header *names* only (the values are never in it;
 * the node starts the server with them empty). The node lists the tools over MCP and stops it within 60 s.
 */
export const toolServerVerifyOrderBodySchema = z.object({
    entryId: z.string().min(1).max(120),
    name: z.string().min(1).max(200),
    server: toolServerDefinitionSchema,
    /** What the entry declares, for the node's own sentence; the platform does the comparison. */
    declared: z.array(z.string().min(1).max(200)).max(500).default([]),
});
/** What a node answers a `toolserver.verify` with, beside its `message`. */
export const toolServerVerifyResultSchema = z.object({
    tools: z.array(toolReportSchema).max(500),
    /** How long the server took to start and list. */
    ms: z.number().nonnegative(),
    /** One line a person reads: "Registered 9 tools in 1.2 s". */
    sentence: z.string().max(500),
    /**
     * The server was reached and answered 401 with a Bearer/OAuth challenge (D490): it asks each person to sign in, so
     * its tools are checked when a person first uses it, and `tools` is empty here. Verify runs with no secrets and
     * never uses anyone's sign-in. `scheme` is the challenge's (Bearer); `resourceMetadata` the protected-resource
     * metadata address it named, when it did.
     */
    signIn: z.object({ scheme: z.string().min(1).max(40), resourceMetadata: z.string().url().max(2000).optional() }).optional(),
});
// ── AIOE (D277): the local model runner, from the workbench's runnerOrders.ts ──
export const RUNNER_CONFIGURE_KIND = "runner.configure";
/** llama.cpp's KV cache types, as src/llm/localRunner/userModels.ts `KV_CACHE_TYPES` lists them. */
const KV_TYPES = ["f32", "f16", "bf16", "q8_0", "q4_0", "q4_1", "iq4_nl", "q5_0", "q5_1"];
export const runnerBackendSchema = z.enum(["cpu", "cuda", "rocm", "vulkan", "metal"]);
/**
 * The runner's settings by the names the CLI, the workbench and the order use. Machine-wide ones are the
 * `localRunner.*` settings; the rest are the selected model's load settings, as the desktop's hub keeps them.
 * A machine-wide value the selected model overrides is written to the model instead, so it takes.
 */
export const RUNNER_SETTINGS = {
    contextSize: { words: "context size (tokens; 0 is the model's own)", kind: "int", min: 0, max: 1_048_576, setting: "localRunner.contextLength", tuning: "contextLength" },
    gpuLayers: { words: "layers on the GPU (99 is all of them)", kind: "int", min: 0, max: 999, setting: "localRunner.gpuLayers", tuning: "gpuLayers" },
    threads: { words: "CPU threads (0 is llama.cpp's choice)", kind: "int", min: 0, max: 1024, setting: "localRunner.threads", tuning: "threads" },
    batchSize: { words: "batch size", kind: "int", min: 1, max: 65_536, tuning: "batchSize" },
    ubatchSize: { words: "physical batch size", kind: "int", min: 1, max: 65_536, tuning: "ubatchSize" },
    parallel: { words: "requests served at once", kind: "int", min: 1, max: 64, tuning: "parallel" },
    flashAttention: { words: "flash attention", kind: "enum", values: ["on", "off", "auto"], tuning: "flashAttn" },
    kvCacheType: { words: "KV cache type", kind: "enum", values: KV_TYPES, tuning: "kvCacheType" },
    port: { words: "port", kind: "int", min: 1024, max: 65_535, setting: "localRunner.port" },
    autoStart: { words: "start with the machine", kind: "bool", setting: "localRunner.autoStart" },
};
export const RUNNER_SETTING_NAMES = Object.keys(RUNNER_SETTINGS);
/** A value for one runner setting, from the CLI's text or an order's JSON; the refusal in words otherwise. */
export function parseRunnerSetting(name, raw) {
    const spec = RUNNER_SETTINGS[name];
    if (!spec)
        return { ok: false, message: `The runner has no setting called ${name}; it has ${RUNNER_SETTING_NAMES.join(", ")}.` };
    if (spec.kind === "bool") {
        if (typeof raw === "boolean")
            return { ok: true, value: raw };
        if (raw === "true" || raw === "on" || raw === "yes")
            return { ok: true, value: true };
        if (raw === "false" || raw === "off" || raw === "no")
            return { ok: true, value: false };
        return { ok: false, message: `${name} (${spec.words}) is true or false, not ${JSON.stringify(raw)}.` };
    }
    if (spec.kind === "enum") {
        const v = raw === true ? "on" : raw === false ? "off" : typeof raw === "string" ? raw.trim().toLowerCase() : undefined;
        if (v !== undefined && spec.values.includes(v))
            return { ok: true, value: v };
        return { ok: false, message: `${name} (${spec.words}) is one of ${spec.values.join(", ")}, not ${JSON.stringify(raw)}.` };
    }
    const n = typeof raw === "number" ? raw : typeof raw === "string" && /^\s*-?\d+\s*$/.test(raw) ? Number(raw) : NaN;
    if (!Number.isInteger(n))
        return { ok: false, message: `${name} (${spec.words}) is a whole number, not ${JSON.stringify(raw)}.` };
    if ((spec.min !== undefined && n < spec.min) || (spec.max !== undefined && n > spec.max))
        return { ok: false, message: `${name} (${spec.words}) is from ${spec.min} to ${spec.max}, not ${n}.` };
    return { ok: true, value: n };
}
const settingsBody = z
    .object({
    contextSize: z.number().int().min(0).max(1_048_576).optional(),
    gpuLayers: z.number().int().min(0).max(999).optional(),
    threads: z.number().int().min(0).max(1024).optional(),
    batchSize: z.number().int().min(1).max(65_536).optional(),
    ubatchSize: z.number().int().min(1).max(65_536).optional(),
    parallel: z.number().int().min(1).max(64).optional(),
    flashAttention: z.union([z.enum(["on", "off", "auto"]), z.boolean()]).optional(),
    kvCacheType: z.enum(KV_TYPES).optional(),
    port: z.number().int().min(1024).max(65_535).optional(),
    autoStart: z.boolean().optional(),
})
    .strict();
/** `runner.configure`: every field optional; at least one. */
export const runnerConfigureBodySchema = z
    .object({
    /** `auto` lets the runner choose (Vulkan for a GPU, Metal on a Mac, else CPU). */
    backend: z.union([runnerBackendSchema, z.literal("auto")]).optional(),
    /** `auto`, `all`, the GPU's number in the machine's `runner.gpus`, its name, or a llama.cpp device id (`CUDA0`). */
    gpu: z.union([z.string().min(1).max(200), z.number().int().min(0).max(63)]).optional(),
    settings: settingsBody.optional(),
    /** The model to load: a catalogue id or a downloaded model's id, or a Hugging Face repository and file (downloaded first). */
    model: z
        .union([
        z.object({ id: z.string().min(1).max(200), name: z.string().max(200).optional() }).strict(),
        z.object({ repo: z.string().min(1).max(200), file: z.string().min(1).max(300), sha256: sha256.optional(), name: z.string().max(200).optional(), catalogued: z.boolean().optional() }).strict(),
    ])
        .optional(),
    /** true: start the runner (restart it when serving, so the change takes); false: stop it. Absent: as it was, restarted when serving and the change needs it. */
    start: z.boolean().optional(),
})
    .strict()
    .refine((b) => b.backend !== undefined || b.gpu !== undefined || (b.settings && Object.keys(b.settings).length > 0) || b.model !== undefined || b.start !== undefined, { message: "a runner.configure order changes something" });
/**
 * AIOE (D273 round C): publishing, and deleting, the Foundry agent of a member of a team the organisation holds
 * (no machine keeps it). Issued only by `POST /admin/v1/teams/:teamId/foundry/publish` and `.../unpublish`,
 * never by the generic routes; only nodes get them. The node does the lifecycle as the organisation's identity
 * and fills the member's `foundry` block through the order's own routes ("Foundry order routes" below).
 */
const foundryAskedBy = z.object({ id: z.string().min(1).max(200), name: z.string().max(200).optional() });
export const foundryPublishOrderBodySchema = z.object({
    teamId: z.string().min(1).max(100),
    /** The member's name in the team (a member has no other id). */
    memberId: z.string().min(1).max(100),
    projectEndpoint: z.string().min(1).max(500),
    askedBy: foundryAskedBy,
});
export const foundryUnpublishOrderBodySchema = foundryPublishOrderBodySchema.extend({ agentName: z.string().min(1).max(200) });
/** The request of the two console routes. */
export const foundryOrderRequestSchema = z.object({ memberId: z.string().min(1).max(100), deviceId: z.string().min(1).max(200) });
/**
 * AIOE (Foundry order routes): what a node carrying out a `foundry.publish` or `foundry.unpublish` reads and
 * writes, with its own token and never as a person. The pending order is the authority, for exactly its team
 * and member:
 *
 *   GET /fleet/v1/orders/:orderId/foundry               -> FoundryOrderView
 *   PUT /fleet/v1/orders/:orderId/foundry/block         { block } (`foundryBlockPutSchema`) -> FoundryOrderView
 *   GET /fleet/v1/orders/:orderId/foundry/credentials   -> FoundryOrderCredentials (cache-control: no-store)
 *
 * Refusals (`error`, with `message` in words): 404 `not_found` (another machine's order, or not a Foundry
 * order); 409 `not_pending`; 410 `team_gone`, `member_gone` or `member_renamed`; credentials only: 403
 * `foundry_not_allowed` (the clause's identity is not the organisation's, or Foundry is not allowed), 409
 * `credentials_missing` naming the missing secrets, 503 `secrets_unavailable`.
 */
export const FOUNDRY_ORG_SECRET_NAMES = ["FOUNDRY_TENANT_ID", "FOUNDRY_CLIENT_ID", "FOUNDRY_CLIENT_SECRET"];
export const FOUNDRY_ORDER_REFUSALS = {
    not_found: "not_found",
    not_pending: "not_pending",
    team_gone: "team_gone",
    member_gone: "member_gone",
    /** The member's name is gone but a Foundry member on the same project endpoint and deployment stands in its place. */
    member_renamed: "member_renamed",
    foundry_not_allowed: "foundry_not_allowed",
    credentials_missing: "credentials_missing",
};
/** `PUT …/foundry/block`: the member's whole `foundry` block (`orgTeamMemberFoundrySchema`), or null to remove it (an unpublish). */
export const foundryBlockPutSchema = z.object({ block: orgTeamMemberFoundrySchema.nullable() });
/**
 * AIOE (D313 teams-one-owner, 0.58.0): refresh the organisation's teams this machine holds, now, instead of at
 * its next read: the teams named, or every one when `teamIds` is left out. Issued by hand from the console's
 * Teams page for a machine shown behind, never automatically; desktops and nodes. Answered `done` with
 * `{ refreshed: n }` in the answer's detail.
 */
export const teamsRefreshOrderBodySchema = z.object({
    teamIds: z.array(z.string().min(1).max(200)).max(500).optional(),
});
const orderBase = {
    id: z.string().min(1).max(100),
    issuedAt: z.string().max(64).optional(),
    /** Who issued it, in words (a person's name or email). */
    by: z.string().max(200).optional(),
    /** AIOE: when the platform stops repeating an order nobody answered (30 days after it was issued). */
    expiresAt: z.string().max(64).optional(),
    /** AIOE: the orders one project-wide request made share a batch id. */
    batchId: z.string().max(100).optional(),
    /** Who asked, as the organisation knows them (their principal), beside `by` in words (D290). */
    askedBy: z.string().max(200).optional(),
};
/** AIOE (D277). */
export const runnerOrderSchema = z.object({ ...orderBase, kind: z.literal(RUNNER_CONFIGURE_KIND), body: runnerConfigureBodySchema });
/** One order on the heartbeat answer's `orders` list. */
export const deviceOrderSchema = z.discriminatedUnion("kind", [
    z.object({ ...orderBase, kind: z.literal("model.download"), body: modelOrderBodySchema }),
    z.object({ ...orderBase, kind: z.literal("model.remove"), body: modelOrderBodySchema }),
    z.object({ ...orderBase, kind: z.literal("runtime.install"), body: runtimeOrderBodySchema }),
    z.object({ ...orderBase, kind: z.literal("runtime.remove"), body: runtimeOrderBodySchema }),
    z.object({ ...orderBase, kind: z.literal("dlp.configure"), body: dlpOrderBodySchema }),
    z.object({ ...orderBase, kind: z.literal("setting.set"), body: settingOrderBodySchema }),
    z.object({ ...orderBase, kind: z.literal("skill.evaluate"), body: skillEvaluateOrderBodySchema }),
    runnerOrderSchema,
    /** AIOE (evaluation). */
    z.object({ ...orderBase, kind: z.literal("specialist.evaluate"), body: specialistEvaluateOrderBodySchema }),
    /** AIOE (Hermes item 1). */
    z.object({ ...orderBase, kind: z.literal("toolserver.verify"), body: toolServerVerifyOrderBodySchema }),
    /** AIOE (D273 round C). */
    z.object({ ...orderBase, kind: z.literal("foundry.publish"), body: foundryPublishOrderBodySchema }),
    z.object({ ...orderBase, kind: z.literal("foundry.unpublish"), body: foundryUnpublishOrderBodySchema }),
    /** AIOE (D313 teams-one-owner). */
    z.object({ ...orderBase, kind: z.literal("teams.refresh"), body: teamsRefreshOrderBodySchema }),
    /** Workbench (D378): start a routine here that allows outside triggers; desktops and nodes. */
    z.object({ ...orderBase, kind: z.literal(ROUTINE_TRIGGER_ORDER_KIND), body: routineTriggerOrderBodySchema }),
    /** Workbench (D401): start a published workflow here (the console's Run now, or an event the organisation forwards); desktops and nodes. The run's id is `runIdForOrder(order.id)`. */
    z.object({ ...orderBase, kind: z.literal(WORKFLOW_START_ORDER_KIND), body: workflowStartOrderBodySchema }),
    /** D290: nodes only (nodeRemote.ts). */
    z.object({ ...orderBase, kind: z.literal("service.configure"), body: serviceConfigureBodySchema }),
    z.object({ ...orderBase, kind: z.literal("update.configure"), body: updateConfigureBodySchema }),
    z.object({ ...orderBase, kind: z.literal("routine.run"), body: routineOrderBodySchema }),
    z.object({ ...orderBase, kind: z.literal("routine.remove"), body: routineOrderBodySchema }),
    z.object({ ...orderBase, kind: z.literal("remote.configure"), body: remoteConfigureBodySchema }),
    z.object({ ...orderBase, kind: z.literal("node.setting.set"), body: nodeSettingBodySchema }),
]);
export const deviceOrderStateSchema = z.enum(["done", "failed", "refused"]);
/** One answer on the heartbeat body's `orders` list. `running` is never sent: an order still running is answered when it ends. */
export const deviceOrderAnswerSchema = z.object({
    id: z.string().min(1).max(100),
    state: deviceOrderStateSchema,
    /** One or two sentences in words: what happened, or which policy refused it. */
    message: z.string().max(500),
    /** AIOE (Foundry order routes): an id for the refusal or failure beside the words (`member_renamed`, `credentials_missing` …); the words stay what a person reads. */
    code: z.string().max(64).optional(),
    /** AIOE: a `skill.evaluate` or (AIOE (evaluation)) a `specialist.evaluate` that ran (`done`) carries what it found; meaningless for any other kind. */
    result: z.union([skillEvaluationResultSchema, specialistEvaluationResultSchema, toolServerVerifyResultSchema]).optional(),
});
/** The `orders` of a heartbeat's answer: each order that parses, in order; anything else is dropped. Also the ids seen, parsed or not. */
export function parseDeviceOrders(raw) {
    const orders = [];
    const ids = [];
    const unreadable = [];
    if (!Array.isArray(raw))
        return { orders, ids, unreadable };
    for (const item of raw.slice(0, MAX_DEVICE_ORDERS)) {
        const id = item && typeof item === "object" && typeof item.id === "string" ? item.id : undefined;
        const parsed = deviceOrderSchema.safeParse(item);
        if (parsed.success) {
            orders.push(parsed.data);
            ids.push(parsed.data.id);
        }
        else if (id && id.length <= 100) {
            ids.push(id);
            unreadable.push(id);
        }
    }
    return { orders, ids, unreadable };
}
// ── AIOE: the platform's additions ────────────────────────────────────
/** The largest body, in bytes of JSON, an order may carry (400 `order_too_large`). */
export const MAX_DEVICE_ORDER_BODY_BYTES = 4096;
/** An order nobody answered is no longer repeated after this long. */
export const DEVICE_ORDER_TTL_MS = 30 * 24 * 3600 * 1000;
/** What the issue routes refuse with, as the `error` of a 400, 409 or 422. */
export const DEVICE_ORDER_REFUSALS = {
    /** 400: a `setting.set` key no held manifest marks lockable (or does not exist). */
    setting_not_lockable: "setting_not_lockable",
    /** 400: a value that looks like a token, a key or a password. */
    secret_refused: "secret_refused",
    /** 400: the kind or body is not valid (`issues` says where); a `dlp.configure` that its manifest clause refuses. */
    invalid_order: "invalid_order",
    /** 422: AI Cloud orders a model from the catalogue's `model` kind only. */
    model_not_approved: "model_not_approved",
    /** 409: `model.remove` / `runtime.remove` of something the machine's facts do not report. */
    not_on_device: "not_on_device",
    /** 400: the body is over 4 KB. */
    order_too_large: "order_too_large",
    /** 400: a `skill.evaluate` sent to the generic routes; it is issued from the catalogue (`POST /admin/v1/catalogue/:id/evaluate`). */
    evaluate_from_catalogue: "evaluate_from_catalogue",
    /** 400: AIOE (evaluation): a `specialist.evaluate` sent to the generic routes; it is issued from the Teams page (`POST /admin/v1/teams/specialists/:id/evaluate`). */
    evaluate_from_teams: "evaluate_from_teams",
    /** 400: AIOE (D273 round C): a `foundry.publish` or `foundry.unpublish` sent to the generic routes; it is issued from the team's page. */
    foundry_from_teams: "foundry_from_teams",
    /** 413: a pending submission's skill does not fit an order inline: publish it first or shorten it. */
    skill_too_large: "skill_too_large",
    /** 403: the node's project classification does not allow the worker's or the judge's provider (the assurance register decides). */
    provider_not_allowed: "provider_not_allowed",
    /** 400: AIOE (D273 round C): the team is kept on a machine, whose workbench publishes its Foundry members. */
    team_on_machine: "team_on_machine",
    /** 400: AIOE (D273 round C): unpublish of an agent built in the Foundry portal. */
    adopted: "adopted",
    /** 400: AIOE (D273 round C): a `runner.configure` naming `extraArgs` or `serverPath`, which a person sets on the machine and an order never carries. */
    not_orderable: "not_orderable",
};
/** The order as the platform stores it: the device's three answer states, and `pending` (not answered) and `expired` (past `expiresAt`, or cancelled). */
export const deviceOrderRecordStateSchema = z.enum(["pending", "done", "failed", "refused", "expired"]);
/** One order as the console reads it (`GET …/orders`, the 201 of an issue). */
export const deviceOrderRecordSchema = z.object({
    id: z.string(),
    deviceId: z.string(),
    kind: deviceOrderKindSchema,
    body: z.record(z.string(), z.unknown()),
    issuedAt: z.string(),
    /** Who issued it: the person's id and, when the platform knows it, their name. */
    issuedBy: z.object({ id: z.string(), name: z.string().optional() }),
    projectId: z.string().optional(),
    batchId: z.string().optional(),
    expiresAt: z.string(),
    state: deviceOrderRecordStateSchema,
    /** The device's sentence, or "cancelled by <name>", or why it expired. */
    message: z.string().optional(),
    answeredAt: z.string().optional(),
});
/** `POST /admin/v1/fleet/devices/:id/orders` and `POST /projects/v1/:id/orders` take this. */
export const deviceOrderIssueSchema = z.object({ kind: deviceOrderKindSchema, body: z.record(z.string(), z.unknown()) });
/** A project batch: one order per node assigned to the project, with the count by state ("3 of 4 done, 1 refused"). */
export const deviceOrderBatchSchema = z.object({
    /** Always present on the 201 of `POST /projects/v1/:id/orders`; absent on `GET …/orders` without `?batchId=` (the project's latest 100 orders). */
    batchId: z.string().optional(),
    orders: z.array(deviceOrderRecordSchema),
    /** Nodes the order was not made for because their last report rules it out (a removal of what they do not hold), with the reason. */
    skipped: z.array(z.object({ deviceId: z.string(), name: z.string(), error: z.string(), message: z.string() })).optional(),
    summary: z.object({
        total: z.number().int().nonnegative(),
        pending: z.number().int().nonnegative(),
        done: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        refused: z.number().int().nonnegative(),
        expired: z.number().int().nonnegative(),
    }),
});
/** The wire form of a stored order: what the heartbeat answer's `orders` carries. */
export function wireDeviceOrder(r) {
    return { id: r.id, kind: r.kind, body: r.body, issuedAt: r.issuedAt, ...(r.issuedBy.name ? { by: r.issuedBy.name } : {}), askedBy: r.issuedBy.id, expiresAt: r.expiresAt, ...(r.batchId ? { batchId: r.batchId } : {}) };
}
// ── What a machine reports about itself ───────────────────────────────
/** One local model on the machine (downloaded), by name and where it came from. */
export const deviceModelFactSchema = z.object({
    name: z.string().min(1).max(200),
    /** The workbench's catalogue id, or the Local models library id. */
    id: z.string().min(1).max(200),
    repo: z.string().max(200).optional(),
    file: z.string().max(300).optional(),
    sizeBytes: z.number().int().nonnegative().optional(),
    /** Serving now. */
    serving: z.boolean().optional(),
});
/** One agent runtime the machine knows of. */
export const deviceRuntimeFactSchema = z.object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    installed: z.boolean(),
    version: z.string().max(100).optional(),
    /** Turned off in settings (by the person or the organisation's policy). */
    disabled: z.boolean().optional(),
    /**
     * Workbench (D414): the named subscription accounts this machine has for the runtime (D136; the usage limit's
     * "Hand off to…" offers them as `sessions.handOff { target: { kind: "account", account: slug } }`), at most 20. Names
     * only, never a credential; `signedIn` is true only when the account's folder holds a sign-in. Absent: none, or a
     * machine too old to say. The runtime's own default sign-in is not listed.
     */
    accounts: z.array(z.object({ slug: z.string().min(1).max(100), name: z.string().min(1).max(100), signedIn: z.boolean() })).max(20).optional(),
});
/** DLP on the machine: the repositories' policies, summed up. */
export const deviceDlpFactSchema = z.object({
    /** Repositories on the machine whose policy classifies prompts / all repositories on it. */
    classifying: z.number().int().nonnegative(),
    repositories: z.number().int().nonnegative(),
    /** The classifier models those policies name. */
    models: z.array(z.string().max(200)).max(20),
    /** Repositories whose policy the organisation manages (cannot be changed here). */
    managed: z.number().int().nonnegative(),
});
/** The machine's memory: system RAM and each GPU. `unified`: GPU and system memory are one pool (Apple Silicon, Strix Halo). */
export const deviceHardwareFactSchema = z.object({
    ramBytes: z.number().int().nonnegative(),
    gpus: z
        .array(z.object({
        name: z.string().max(200),
        vendor: z.enum(["nvidia", "amd", "apple", "intel", "other"]).optional(),
        vramBytes: z.number().int().nonnegative().optional(),
    }))
        .max(8),
    unified: z.boolean().optional(),
});
/** AIOE (D277): the runner's facts, from the workbench's runnerOrders.ts. */
export const runnerGpuFactSchema = z.object({
    index: z.number().int().min(0).max(63),
    name: z.string().min(1).max(200),
    vendor: z.enum(["nvidia", "amd", "intel", "apple", "other"]),
    memoryMB: z.number().int().nonnegative().optional(),
    driver: z.string().max(100).optional(),
    compute: z.string().max(100).optional(),
    integrated: z.boolean().optional(),
});
/** `facts.runner`: what the console offers choices from. */
export const runnerFactsSchema = z.object({
    /** The backends this machine can run (a GPU of the vendor's kind and a published llama.cpp build). */
    backends: z.array(runnerBackendSchema).max(5),
    /** What is set: `auto` lets the runner choose. */
    backend: z.union([runnerBackendSchema, z.literal("auto")]),
    /** What runs (or would run) under that setting. */
    backendInUse: runnerBackendSchema.optional(),
    gpus: z.array(runnerGpuFactSchema).max(32),
    /** `auto`, `all`, or the llama.cpp device id chosen (`CUDA0`). */
    gpu: z.string().max(100),
    /** The GPU that id is, in words, when known. */
    gpuName: z.string().max(200).optional(),
    model: z.object({ id: z.string().max(200), name: z.string().max(200) }).optional(),
    settings: settingsBody.extend({ flashAttention: z.enum(["on", "off", "auto"]).optional() }),
    serving: z.boolean(),
    /** Which checks could not be made, in words ("vulkaninfo is not installed, so …"). */
    unchecked: z.array(z.string().max(300)).max(10).optional(),
});
/** `facts` on a registration: absent until the machine has measured them; the platform keeps the last. */
export const deviceFactsSchema = z.object({
    models: z.array(deviceModelFactSchema).max(100),
    runtimes: z.array(deviceRuntimeFactSchema).max(50),
    dlp: deviceDlpFactSchema.optional(),
    /** What the machine can run a model on (additive; modelFit.ts reads it). Absent until the machine reports it. */
    hardware: deviceHardwareFactSchema.optional(),
    /** AIOE (D277): the local model runner: its backends, GPUs, model and settings. Absent until the machine reports it. */
    runner: runnerFactsSchema.optional(),
    /**
     * Workbench (D378): the published routines (catalogue ids, D363) this machine holds that let the organisation start
     * them on an event, at most 50; a `routine.trigger` order need go only to machines that list it. Absent from a machine
     * too old to say: it may still hold one.
     */
    routines: z.object({ outside: z.array(z.string().min(1).max(200)).max(50) }).optional(),
    /**
     * Workbench (D401): the workflows this machine holds (catalogue ids, each with the versions it holds), at most 200; a
     * `workflow.start` order need go only to machines that list it, in the version it names. Absent from a machine too old to
     * say: it may still hold one. An empty `held` means it holds none.
     */
    workflows: workflowFactsSchema.optional(),
    measuredAt: z.string().max(64),
});
// ── The organisation's approved local models (catalogue kind `model`) ─────
/** One entry of the organisation's catalogue kind `model`: what the console offers and AI Cloud orders from. */
export const approvedModelSchema = z.object({
    name: z.string().min(1).max(200),
    repo: z.string().min(1).max(200),
    file: z.string().min(1).max(300),
    sha256: sha256.optional(),
    sizeBytes: z.number().int().nonnegative().optional(),
    /** The context the fit estimate assumes (modelFit.ts); 8192 when left out. */
    contextLength: z.number().int().positive().max(1_000_000).optional(),
    /** What it is for, in words: "coding", "DLP classifier", "memory". */
    use: z.string().max(100).optional(),
});
// ── Words ─────────────────────────────────────────────────────────────
/** What an order asks, as the end of "Your organisation asked this machine to …". */
export function describeDeviceOrder(order, runtimeName = (id) => id) {
    switch (order.kind) {
        case "model.download":
            return `download ${order.body.name}`;
        case "model.remove":
            return `remove ${order.body.name}`;
        case "runtime.install":
            return `install ${order.body.name ?? runtimeName(order.body.id)}`;
        case "runtime.remove":
            return `remove ${order.body.name ?? runtimeName(order.body.id)}`;
        case "dlp.configure":
            return describeDlpChange(order.body);
        case "setting.set":
            return order.body.value === null ? `clear its own value of the setting ${order.body.key}` : `set ${order.body.key} to ${JSON.stringify(order.body.value)}`;
        case "skill.evaluate": {
            const n = order.body.tasks.length;
            return n ? `evaluate the skill ${order.body.name} on ${n} ${n === 1 ? "task" : "tasks"}` : `evaluate the skill ${order.body.name} on tasks its judge writes`;
        }
        case "runner.configure":
            return describeRunnerOrder(order.body);
        case "toolserver.verify":
            return `start the tool server ${order.body.name} with no secrets and list what it offers`;
        case "specialist.evaluate":
            return `evaluate the specialist ${order.body.specialistName} against dataset ${order.body.datasetName}${order.body.caseLimit ? ` (the first ${order.body.caseLimit} ${order.body.caseLimit === 1 ? "case" : "cases"})` : ""}`;
        case "foundry.publish":
            return `publish the Foundry agent for ${order.body.memberId} to ${endpointHost(order.body.projectEndpoint)}`;
        case "foundry.unpublish":
            return `delete the Foundry agent ${order.body.agentName} from ${endpointHost(order.body.projectEndpoint)}`;
        case "teams.refresh": {
            const n = order.body.teamIds?.length ?? 0;
            return n ? `refresh ${n === 1 ? "one team" : `${n} teams`} from the organisation now` : "refresh every team it holds from the organisation now";
        }
        case ROUTINE_TRIGGER_ORDER_KIND:
            return `start the routine ${order.body.routineId} for ${order.body.event.summary}`;
        /** Workbench (D401). */
        case WORKFLOW_START_ORDER_KIND:
            return `start the workflow ${order.body.workflowId}${order.body.version ? ` ${order.body.version}` : ""}${order.body.event ? ` for ${order.body.event.summary}` : ""}`;
        case "service.configure":
        case "update.configure":
        case "routine.run":
        case "routine.remove":
        case "remote.configure":
        case "node.setting.set":
            return describeNodeOrder(order);
    }
}
function endpointHost(endpoint) {
    try {
        return new URL(endpoint).hostname;
    }
    catch {
        return endpoint;
    }
}
/** A DLP change in words. */
export function describeDlpChange(body) {
    const parts = [];
    if (body.classify === false)
        parts.push("turn DLP classification off");
    else if (body.classify === true)
        parts.push(`turn DLP classification on${body.classifier?.model ? ` with ${body.classifier.model}` : ""}`);
    else if (body.classifier?.model)
        parts.push(`classify prompts with ${body.classifier.model}`);
    if (body.onUnclassified)
        parts.push(body.onUnclassified === "block" ? "stop prompts the classifier cannot check" : body.onUnclassified === "ask" ? "ask before sending prompts the classifier cannot check" : "let unchecked prompts through");
    if (body.onSecrets)
        parts.push(`${body.onSecrets} secrets`);
    if (body.onPii)
        parts.push(`${body.onPii} personal information`);
    if (!parts.length)
        return "change its DLP settings";
    return parts.join(", ");
}
// AIOE (D277): a runner.configure in words, from the workbench's runnerOrders.ts.
const BACKEND_WORDS = { cpu: "the CPU", cuda: "CUDA", rocm: "ROCm", vulkan: "Vulkan", metal: "Metal", auto: "the automatic choice" };
/** What a `runner.configure` asks, as the end of "Your organisation asked this machine to …". */
export function describeRunnerOrder(body) {
    const parts = [];
    if (body.model)
        parts.push(`load ${body.model.name ?? ("id" in body.model ? body.model.id : `${body.model.repo} ${body.model.file}`)} in its local model`);
    if (body.backend)
        parts.push(`run its local model on ${BACKEND_WORDS[body.backend]}`);
    if (body.gpu !== undefined)
        parts.push(String(body.gpu) === "auto" ? "let the runner choose the GPU" : String(body.gpu) === "all" ? "split its local model over every GPU" : `use GPU ${body.gpu} for its local model`);
    const settings = Object.entries(body.settings ?? {});
    if (settings.length)
        parts.push(`set its local model's ${settings.map(([k, v]) => `${RUNNER_SETTINGS[k]?.words.replace(/ \(.*\)$/, "") ?? k} to ${typeof v === "boolean" && k === "flashAttention" ? (v ? "on" : "off") : String(v)}`).join(", ")}`);
    if (body.start === true)
        parts.push("start its local model");
    if (body.start === false)
        parts.push("stop its local model");
    if (!parts.length)
        return "change its local model";
    return parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}
/** The sentence a person reads: "Your organisation asked this machine to download Qwen 2.5 Coder 14B — done." */
export function deviceOrderSentence(order, answer, runtimeName) {
    const asked = `Your organisation asked this machine to ${describeDeviceOrder(order, runtimeName)}`;
    if (!answer)
        return `${asked} — working on it.`;
    return sentenceTail(asked, answer.state, answer.message);
}
/** "<asked> — done." / "<asked> — refused: <why>." / "<asked> — it did not work: <why>." */
export function sentenceTail(asked, state, message) {
    const tail = state === "done" ? "done" : state === "refused" ? "refused" : "it did not work";
    const why = message.trim().replace(/\.$/, "");
    return `${asked} — ${tail}${why && state !== "done" ? `: ${why}` : ""}.`;
}
/** parseDeviceOrders reads `runner.configure` itself. */
export const parseWorkbenchDeviceOrders = parseDeviceOrders;
/** `facts.hardware`: the shape above (deviceHardwareFactSchema). */
export const hardwareFactsSchema = deviceHardwareFactSchema;
/** The facts a workbench sends: `runner` and `hardware` are on deviceFactsSchema. */
export const workbenchDeviceFactsSchema = deviceFactsSchema;
//# sourceMappingURL=deviceOrders.js.map